/**
 * Provides the `getFormFields` utility function for extracting the current values of named form fields within a given HTMLFormElement, and its associated `GetFormFieldsElement` type.
 * @module getFormFields
 */
// Import global utilities
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Extracts the current values of named form fields within a given HTMLFormElement and returns them as a key-value object.
 *
 * **Behavior:**
 * - Takes an `HTMLFormElement` (or `null`) as input.
 * - If the input `$form` is null or undefined (checked via `isDefined`), returns an empty object `{}` immediately.
 * - Selects all `input`, `textarea`, and `select` elements within the form using `querySelectorAll`.
 * - Iterates through the selected field elements.
 * - For each element:
 * - Retrieves the `name` attribute. If the name is missing or empty (checked via `isEmpty`), the element is skipped.
 * - **Radio buttons & checkboxes:** If the element is an `input` of type `radio` or `checkbox`, it checks the `checked` property. If the element is *not* checked, it's skipped.
 * - **Value assignment:** For all other elements (text inputs, textareas, selects, hidden inputs, *and* checked radio/checkboxes), the current `value` property of the element is assigned to the results object, using the element's `name` attribute as the key.
 * - **Duplicate names:** If multiple fields share the same `name`, the value of the *last* element encountered in the `querySelectorAll` NodeList will overwrite any previous values for that name in the results object. This is particularly relevant for radio button groups (where only the checked one should be included anyway) and potentially for checkboxes if multiple with the same name are checked (the last checked one's value wins).
 * - Returns the populated `Record<string, string>` object containing the name-value pairs.
 *
 * @since 3.8 Initial implementation.
 * @param {HTMLFormElement | null} $form - The `<form>` element from which to extract field values. Can be `null`.
 * @return {Record<string, string>} An object where keys are the `name` attributes of the form fields and values are their corresponding current string values. Returns an empty object `{}` if the input form is null/undefined or contains no named fields meeting the criteria.
 *
 * @example Importing the getFormFields utility
 * ```typescript
 * import { getFormFields } from '@automator/shared/utils/get-form-fields';
 * ```
 *
 * @example Extracting from a standard form
 * Gets values from text input, textarea, and selected dropdown option.
 * Assuming the following HTML:
 * ```html
 * <form id="form">
 *     <input name="user" value="Da Vinci">
 *     <textarea name="msg">Hi</textarea>
 *     <select name="opt">
 *         <option value="1" selected>One</option>
 *     </select>
 * </form>
 * ```
 * ```typescript
 * const $form = document.getElementById( 'form' ); // HTMLFormElement
 * const formData = getFormFields( $form );
 * // => { user: "Da Vinci", msg: "Hi", opt: "1" }
 * ```
 *
 * @example Handling radio buttons
 * Only the value of the *checked* radio button in a group is included.
 * Assuming the following HTML:
 * ```html
 * <form id="radio-form">
 *     <input type="radio" name="color" value="R">
 *     <input type="radio" name="color" value="G" checked>
 *     <input type="radio" name="color" value="B">
 * </form>
 * ```
 * ```typescript
 * const $form = document.getElementById( 'radio-form' ); // HTMLFormElement
 * const formData = getFormFields( $form );
 * // => { color: "G" }
 * ```
 *
 * @example Handling checkboxes
 * Only the values of *checked* checkboxes are included. This follows the behavior of the native `FormData` object, as defined in the [HTML specification](https://www.w3.org/TR/html401/interact/forms.html#checkbox).
 * If multiple with the same name are checked, the last one's value is used.
 * Assuming the following HTML:
 * ```html
 * <form id="check-form">
 *     <input type="checkbox" name="terms" value="agreed" checked>
 *     <input type="checkbox" name="prefs" value="email">
 *     <input type="checkbox" name="prefs" value="sms" checked>
 * </form>
 * ```
 * ```typescript
 * const $form = document.getElementById( 'check-form' ); // HTMLFormElement
 * const formData = getFormFields( $form );
 * // => { terms: "agreed", prefs: "sms" }
 * ```
 *
 * @example Checkbox without value attribute
 * A checked checkbox without a `value` attribute defaults to the value "on".
 * Assuming the following HTML:
 * ```html
 * <form id="default-check">
 *     <input type="checkbox" name="toggle" checked>
 * </form>
 * ```
 * ```typescript
 * const $form = document.getElementById( 'default-check' ); // HTMLFormElement
 * const formData = getFormFields( $form );
 * // => { toggle: "on" }
 * ```
 *
 * @example Handling null or empty form
 * Returns an empty object if the form element is null or doesn't exist.
 * ```typescript
 * const formData = getFormFields( null );
 * // => {}
 * const $emptyForm = document.createElement( 'form' );
 * const formDataEmpty = getFormFields( $emptyForm );
 * // => {}
 * ```
 *
 * @remarks
 * - This function relies on standard DOM APIs (`querySelectorAll`, `getAttribute`, element properties like `value`, `type`, `checked`).
 * - It provides a snapshot of the form field values *at the time it is called*.
 * - Limitation: It does not handle `select-multiple` elements (it will return the value of the first selected option only).
 * - Limitation: It does not handle file inputs (`<input type="file">`); the `.value` property would contain a fake path for security reasons.
 */
export function getFormFields($form) {
    const fields = {};
    // If the form element is not defined (e.g., null or undefined), return an empty object immediately.
    if (!isDefined($form)) {
        return fields;
    }
    // Iterate over all input, textarea, and select elements within the form.
    const $fields = $form.querySelectorAll('input, textarea, select');
    $fields.forEach($field => {
        const fieldName = $field.getAttribute('name');
        if (isEmpty(fieldName)) {
            return;
        }
        // If the field is an HTMLInputElement (like checkbox or radio) and is not checked, skip it.
        if ($field instanceof HTMLInputElement &&
            ['checkbox', 'radio'].includes($field.type) &&
            !$field.checked) {
            return;
        }
        fields[fieldName] = $field.value;
    });
    return fields;
}
;
